import * as THREE from 'three';
import { WORLD, MINERAL_DEFS, HOOK_HIT_RADIUS, type MineralKind } from './config';
import type { Mineral } from './minerals';
import { clamp, lerp } from './math';
import { createPixelSprite } from './pixelAssets';

export type HookState = 'swinging' | 'extending' | 'retracting' | 'cooldown';

export interface HookOwner {
  id: number;
  pivotX: number;
  color: string;
}

export class Hook {
  state: HookState = 'swinging';
  angle = 0;
  private swingPhase = 0;
  private swingDir = 1;
  /** Direction sampled on fire; stays fixed until this shot returns. */
  private shotAngle = 0;
  length = 1.2;
  private maxReach = WORLD.maxHookLength;
  private retractBase = WORLD.baseRetractSpeed;
  /** target end position */
  readonly tip = new THREE.Vector3();
  private grabbed: Mineral | null = null;
  private weightFactor = 1;
  private magnetTimer = 0;
  private gravityFlip = false;
  private twinActive = false;
  private blastArmed = false;
  private stuckTimer = 0;
  private animTime = 0;
  private swingMultiplier = 1;
  private rapidReturn = false;
  private grabOrigin: {x:number;y:number} | null = null;

  readonly group = new THREE.Group();
  private readonly arm: THREE.Mesh;
  private readonly miner: THREE.Sprite;
  private readonly chainMat: THREE.LineBasicMaterial;
  private readonly chainGeo: THREE.BufferGeometry;
  private readonly chain: THREE.Line;
  private readonly claw: THREE.Group;

  constructor(readonly owner: HookOwner) {
    this.group.position.set(owner.pivotX, WORLD.pivotY, 0);

    const armMat = new THREE.MeshStandardMaterial({
      color: owner.color,
      emissive: owner.color,
      emissiveIntensity: 0.55,
      metalness: 0.4,
      roughness: 0.35,
    });
    this.arm = new THREE.Mesh(new THREE.BoxGeometry(0.55, 1.1, 0.55), armMat);
    this.arm.position.y = -0.2;
    this.group.add(this.arm);

    // Winch housing
    const winch = new THREE.Mesh(
      new THREE.CylinderGeometry(0.55, 0.55, 0.45, 16),
      new THREE.MeshStandardMaterial({
        color: '#1A2438',
        emissive: owner.color,
        emissiveIntensity: 0.25,
        metalness: 0.6,
        roughness: 0.4,
      }),
    );
    winch.rotation.z = Math.PI / 2;
    winch.position.y = 0.35;
    this.arm.visible = false;
    winch.visible = false;
    const miner = this.miner = createPixelSprite(owner.id === 0 ? 10 : 11, 2.4);
    miner.position.set(0, 0.9, 0.2);
    this.group.add(miner);

    this.chainGeo = new THREE.BufferGeometry();
    this.chainMat = new THREE.LineBasicMaterial({
      color: owner.color,
      transparent: true,
      opacity: 0.85,
    });
    this.chain = new THREE.Line(this.chainGeo, this.chainMat);
    this.group.add(this.chain);

    this.claw = this.createClaw(owner.color);
    this.group.add(this.claw);
    this.updateVisuals();
  }

  private createClaw(color: string): THREE.Group {
    const claw = new THREE.Group();
    const mat = new THREE.MeshStandardMaterial({
      color,
      emissive: color,
      emissiveIntensity: 0.7,
      metalness: 0.55,
      roughness: 0.3,
    });
    const core = new THREE.Mesh(new THREE.OctahedronGeometry(0.28, 0), mat);
    claw.add(core);
    for (let i = 0; i < 3; i++) {
      const prong = new THREE.Mesh(new THREE.BoxGeometry(0.12, 0.55, 0.12), mat);
      const a = (i / 3) * Math.PI * 2;
      prong.position.set(Math.cos(a) * 0.22, -0.28, Math.sin(a) * 0.22);
      prong.rotation.z = Math.cos(a) * 0.45;
      prong.rotation.x = -Math.sin(a) * 0.45;
      claw.add(prong);
    }
    return claw;
  }

  get grabbedMineral(): Mineral | null {
    return this.grabbed;
  }

  setDifficulty(multiplier:number):void { this.swingMultiplier=multiplier; }

  /** Interrupt an empty outgoing shot without changing its locked direction. */
  interrupt(delay:number):void { if(this.state!=='extending')return;this.state='retracting';this.stuckTimer=delay; }

  cutRope():boolean {
    if(this.state!=='extending'&&this.state!=='retracting')return false;
    if(this.grabbed){
      const m=this.grabbed;m.grabbedBy=null;
      if(this.grabOrigin){m.x=this.grabOrigin.x;m.y=this.grabOrigin.y;}
      m.group.position.set(m.x,m.y,0);m.group.rotation.z=0;this.grabbed=null;
    }
    this.weightFactor=1;this.stuckTimer=0;this.rapidReturn=true;this.state='retracting';return true;
  }

  setSurfaceRatio(ratio: number): void { this.miner.position.y = 0.9 + (0.26 - ratio) * 25; }

  setMagnet(on: boolean, duration: number): void {
    this.magnetTimer = on ? duration : 0;
  }

  setGravityFlip(on: boolean): void {
    this.gravityFlip = on;
    if (this.grabbed) this.updateWeightFactor(this.grabbed.kind);
  }

  setTwin(on: boolean): void {
    this.twinActive = on;
  }

  setBlast(on: boolean): void {
    this.blastArmed = on;
  }

  consumeBlast(): boolean {
    if (!this.blastArmed) return false;
    this.blastArmed = false;
    return true;
  }

  get isTwin(): boolean {
    return this.twinActive;
  }

  fire(): void {
    if (this.state !== 'swinging') return;
    this.shotAngle = this.angle;
    this.rapidReturn = false;
    this.state = 'extending';
    this.length = 1.2;
    this.stuckTimer = 0;
  }

  reset(pivotX: number): void {
    this.owner.pivotX = pivotX;
    this.group.position.x = pivotX;
    this.state = 'swinging';
    this.length = 1.2;
    if (this.grabbed) {
      this.grabbed.grabbedBy = null;
      this.grabbed = null;
    }
    this.swingPhase = 0.5;
    this.swingDir = 1;
    this.angle = 0;
    this.shotAngle = 0;
    this.weightFactor = 1;
    this.rapidReturn=false;this.grabOrigin=null;
    this.magnetTimer = 0;
    this.gravityFlip = false;
    this.twinActive = false;
    this.blastArmed = false;
    this.stuckTimer = 0;
    this.updateVisuals();
  }

  update(
    dt: number,
    timeScale: number,
    minerals: Mineral[],
    onGrab: (m: Mineral) => void,
    onDeposit: (m: Mineral) => void,
    onBlast: (x: number, y: number) => void,
  ): void {
    const dtS = dt * (this.state==='swinging'?timeScale:1);
    this.animTime += dtS;

    if (this.magnetTimer > 0) this.magnetTimer = Math.max(0, this.magnetTimer - dtS);

    switch (this.state) {
      case 'swinging': {
        this.swingPhase += dtS * WORLD.swingSpeed * this.swingMultiplier * this.swingDir;
        if (this.swingPhase > 1) {
          this.swingPhase = 1;
          this.swingDir = -1;
        } else if (this.swingPhase < 0) {
          this.swingPhase = 0;
          this.swingDir = 1;
        }
        // Ease swing for nicer feel
        const t = this.swingPhase;
        const eased = t * t * (3 - 2 * t);
        this.angle = lerp(-WORLD.swingAmplitude, WORLD.swingAmplitude, eased);
        this.length = 1.2 + Math.sin(this.animTime * 2.2) * 0.06;
        break;
      }
      case 'extending': {
        this.length += WORLD.extendSpeed * dtS;
        const tip = this.computeTip();
        this.tip.copy(tip);

        // Magnet attraction for small items
        if (this.magnetTimer > 0) {
          for (const m of minerals) {
            if (!m.active || m.hiddenBy !== null || m.grabbedBy !== null) continue;
            if (m.weight > 2.2) continue;
            const dx = m.x - tip.x;
            const dy = m.y - tip.y;
            const dist = Math.hypot(dx, dy);
            if (dist < 2.4 && dist > 0.01) {
              m.x -= (dx / dist) * 2.2 * dtS;
              m.y -= (dy / dist) * 2.2 * dtS;
              m.group.position.set(m.x, m.y, 0);
            }
          }
        }

        // Collision with minerals
        for (const m of minerals) {
          if (!m.active || m.hiddenBy !== null || m.grabbedBy !== null) continue;
          const dx = m.x - tip.x;
          const dy = m.y - tip.y;
          const hitR = m.radius + HOOK_HIT_RADIUS;
          if (dx * dx + dy * dy < hitR * hitR) {
            if (m.kind === 'rock' && this.blastArmed) {
              onBlast(m.x, m.y);
              this.consumeBlast();
              // destroy rock and continue? Classic: blast destroys and hook returns empty
              this.state = 'retracting';
              this.grabbed = null;
              // mark rock inactive via blast callback
              break;
            }
            if (m.kind === 'dynamite') {
              onGrab(m); // special: explodes chain back
              this.grabbed = null;
              this.state = 'retracting';
              this.length = Math.max(1.2, this.length - 2);
              break;
            }
            this.grabbed = m;
            this.grabOrigin={x:m.x,y:m.y};
            m.grabbedBy = this.owner.id;
            this.state = 'retracting';
            this.updateWeightFactor(m.kind);
            onGrab(m);
            break;
          }
        }

        if (this.length >= this.maxReach || Math.abs(this.tip.x) > WORLD.halfWidth || this.tip.y < WORLD.floorY + 0.5) {
          this.state = 'retracting';
          this.grabbed = null;
        }
        break;
      }
      case 'retracting': {
        if (this.stuckTimer > 0) {
          this.stuckTimer -= dtS;
          break;
        }
        const speed = this.retractBase * this.weightFactor * (this.twinActive ? 1.55 : 1) * (this.rapidReturn ? 2.4 : 1);
        this.length -= speed * dtS;
        if (this.grabbed) {
          const tip = this.computeTip();
          this.grabbed.x = tip.x;
          this.grabbed.y = tip.y;
          this.grabbed.group.position.set(tip.x, tip.y, 0);
          this.grabbed.group.rotation.z = Math.sin(this.animTime * 6) * 0.15;
        }
        if (this.length <= 1.25) {
          this.length = 1.2;
          if (this.grabbed) {
            onDeposit(this.grabbed);
            this.grabbed.grabbedBy = null;
            this.grabbed = null;
          }
          this.state = 'swinging';
          this.weightFactor = 1;
        }
        break;
      }
      case 'cooldown':
        this.length = lerp(this.length, 1.2, 0.15);
        if (this.length < 1.3) this.state = 'swinging';
        break;
    }

    this.updateVisuals();
  }

  private updateWeightFactor(kind: MineralKind): void {
    let weight = MINERAL_DEFS[kind].weight;
    if (this.gravityFlip) weight = weight < 2 ? weight * 2.2 : weight * 0.35;
    this.weightFactor = clamp(1 / Math.max(0.35, weight * 0.28), 0.18, 1.2);
  }

  computeTip(out = new THREE.Vector3()): THREE.Vector3 {
    const angle = this.directionAngle;
    const dirX = Math.sin(angle);
    const dirY = -Math.cos(angle);
    out.set(
      this.owner.pivotX + dirX * this.length,
      WORLD.pivotY + dirY * this.length,
      0,
    );
    return out;
  }

  /** The rope, claw, collision ray and tool prediction all share this angle. */
  get directionAngle(): number {
    return this.state === 'swinging' ? this.angle : this.shotAngle;
  }

  private updateVisuals(): void {
    const tip = this.computeTip();
    const angle = this.directionAngle;
    this.tip.copy(tip);
    this.claw.position.set(tip.x - this.owner.pivotX, tip.y - WORLD.pivotY, 0);
    // The claw's forward axis is local -Y. Rotating it by +angle aligns it
    // with the rope vector (sin(angle), -cos(angle)).
    this.claw.rotation.z = angle;

    // Chain line from pivot to tip
    const positions = new Float32Array(6);
    positions[0] = 0;
    positions[1] = 0;
    positions[2] = 0;
    positions[3] = tip.x - this.owner.pivotX;
    positions[4] = tip.y - WORLD.pivotY;
    positions[5] = 0;
    this.chainGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));

    // Arm visual points toward chain
    this.arm.rotation.z = angle;
    this.arm.position.set(Math.sin(angle) * 0.35, -0.2 - Math.cos(angle) * 0.15, 0);
    this.chainMat.opacity = this.state === 'swinging' ? 0.55 : 0.9;
  }

  dispose(): void {
    this.group.traverse((obj) => {
      if (obj instanceof THREE.Mesh) {
        obj.geometry.dispose();
        const materials = Array.isArray(obj.material) ? obj.material : [obj.material];
        materials.forEach((m) => m.dispose());
      } else if (obj instanceof THREE.Sprite) (obj.material as THREE.SpriteMaterial).dispose();
    });
    this.chainGeo.dispose();
    this.chainMat.dispose();
  }

  stabilize(): void {
    this.swingPhase = 0.5;
    this.angle = 0;
    this.shotAngle = 0;
    this.length = 1.2;
    this.updateVisuals();
  }
}

export function weightLabel(kind: MineralKind): string {
  const w = MINERAL_DEFS[kind].weight;
  if (w < 1) return '极轻';
  if (w < 1.6) return '轻';
  if (w < 3) return '中';
  if (w < 5) return '重';
  return '极重';
}
